import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import NotificationDropdown from './NotificationDropdown';
import notificationService from '../../services/notificationService';

/**
 * Cloche de notifications — version optimisée :
 * - React Query pour le compteur (cache partagé, dédup entre instances) ;
 * - polling suspendu quand l'onglet est masqué (refetchIntervalInBackground:false
 *   + invalidation au retour de visibilité) ;
 * - la liste récente n'est chargée que lorsque le menu est ouvert (lazy).
 */
const NotificationIcon: React.FC = () => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Compteur non lus — rafraîchi toutes les 60s, uniquement onglet visible
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  // Liste récente — chargée seulement quand le menu est ouvert
  const { data: notifications = [], isFetching: loading } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => notificationService.getRecentNotifications(),
    enabled: isOpen,
    staleTime: 30_000,
  });

  // Au retour sur l'onglet, rafraîchir le compteur immédiatement
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [queryClient]);

  // Fermer le menu au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = useMutation({
    mutationFn: (id: number) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high':   return 'bg-orange-500';
      case 'medium': return 'bg-blue-500';
      case 'low':    return 'bg-gray-400';
      default:       return 'bg-blue-500';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="relative flex items-center justify-center w-10 h-10 rounded-xl border border-[var(--border)] bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationDropdown
          notifications={notifications}
          loading={loading}
          onMarkAsRead={(id) => markAsRead.mutate(id)}
          onMarkAllAsRead={() => markAllAsRead.mutate()}
          onClose={() => setIsOpen(false)}
          getPriorityColor={getPriorityColor}
        />
      )}
    </div>
  );
};

export default NotificationIcon;
